// Ügyfél-detail „Bankkártyák" szekció (`?clientId={id}`).
//
// Három slot (`RelatedList`): **birtokos (+ maszkolt kártyaszám alcím) · bank · lejárat**.
//
// A KÁRTYASZÁM SOHA nem teljes: a rendszer csak az utolsó 4 jegyet tárolja, és itt is `•••• 1234` alakban
// jelenik meg — a maszk nem UI-dísz, hanem az adatmodell tükre.
//
// A LEJÁRAT a jobb szélső (státusz) slotban van, mert ez a kártya egyetlen „állapot"-jellegű információja:
// a lejárt kártyáról a számlázás elhasal. A lejárt/hamarosan lejáró állapotot a lejáratból SZÁMÍTJUK (nincs
// hozzá backend-mező) — ugyanaz a szabály, mint a Bankkártyák lista-oldalon.
import { type PaymentCardRowSchema } from '@atlas/contracts';
import { CreditCardIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { PaymentCardFormSheet } from '@/components/payment-cards/payment-card-form-sheet';
import { DomainBadge } from '@/components/shared/domain-badge';
import { FormSection } from '@/components/shared/form-section';
import { RelatedList, type RelatedListItem } from '@/components/shared/related-list';
import { Button } from '@/components/ui/button';
import { CLIENT_SECTION } from '@/features/clients/sections';
import { paymentCardKeys } from '@/features/payment-cards/api';
import { api } from '@/lib/api';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

type PaymentCardRow = z.infer<typeof PaymentCardRowSchema>;

/**
 * A lejárat állapota a hónap pontosságú lejáratból. `expired` = a lejárat hónapja MÁR elmúlt; `soon` = az
 * aktuális vagy a következő hónap; egyébként `valid`. Hiányzó lejárat → nincs jel (nem tudunk állítani semmit).
 */
function expiryTone(
  month: number | null,
  year: number | null,
  now: Date,
): { label: string; status: 'valid' | 'expiring' | 'expired' } | null {
  if (month === null || year === null) return null;
  const label = `${String(month).padStart(2, '0')}/${year}`;
  const monthsLeft = (year - now.getFullYear()) * 12 + (month - (now.getMonth() + 1));
  if (monthsLeft < 0) return { label, status: 'expired' };
  if (monthsLeft <= 1) return { label, status: 'expiring' };
  return { label, status: 'valid' };
}

export function ClientPaymentCardsSection({
  clientId,
  clientName,
}: {
  clientId: string;
  clientName: string;
}): JSX.Element {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canManage = can('payment_cards.manage');

  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<PaymentCardRow | null>(null);

  const listKey = [...paymentCardKeys.lists(), 'by-client', clientId];
  const query = api.paymentCards.useQuery(listKey, {
    query: { clientId, sort: 'holderName', order: 'asc', limit: 200 },
  });
  const rows: PaymentCardRow[] = query.data?.status === 200 ? query.data.body.cards : [];
  const errStatus = query.error?.status;

  const now = new Date();
  const items: RelatedListItem[] = rows.map((c) => {
    const expiry = expiryTone(c.expiryMonth, c.expiryYear, now);
    return {
      id: c.id,
      title: c.holderName ?? '—',
      subtitle: c.last4 ? `•••• ${c.last4}` : undefined,
      meta: [c.brand, c.bankName].filter(Boolean).join(' · '),
      status: expiry ? (
        <DomainBadge
          kind="paymentCardStatus"
          value={expiry.status}
          label={expiry.label}
          className="tabular-nums"
        />
      ) : undefined,
    };
  });

  return (
    <FormSection id={CLIENT_SECTION.paymentCards} title={t('clients.paymentCardsTitle')}>
      <RelatedList
        items={items}
        isLoading={query.isLoading}
        isError={query.isError}
        forbidden={isForbiddenStatus(errStatus)}
        onRetry={() => void query.refetch()}
        onItemClick={
          canManage ? (item) => setEditRow(rows.find((r) => r.id === item.id) ?? null) : undefined
        }
        // NINCS kattintható belépő: a modul lista-oldalán ma nincs ügyfél-szűrő, tehát a „megtekintés" a
        // teljes, szűretlen listára dobna — az rosszabb, mint a puszta darabszám. (Ha a lista-oldal kap
        // ügyfél-szűrőt, ide is bekerül az `onShowAll`, mint a Szolgáltatásoknál.)
        showAllLabel={(total) => t('clients.relatedTotal', { count: total })}
        emptyLabel={t('clients.paymentCardsEmpty')}
        emptyIcon={CreditCardIcon}
        errorLabel={t('clients.relatedError')}
        forbiddenLabel={t('clients.relatedForbidden')}
        retryLabel={t('clients.relatedRetry')}
      />

      {/* A „hozzáadás" belépő a szekció TARTALMÁNAK VÉGÉN, BALRA (user-döntés, 2026-08-18) — ugyanaz a
          gomb-alak, mint a profil „Jelszó módosítása" / „2FA beállítása" gombja. Korábban a fejléc jobb
          szélén ült: ott vizuálisan nehezebb volt a halk `overline` címnél, és négy szekciónál egy egész
          gomb-oszlop uralta a lapot. A tartalom végén viszont ott van, ahol a felhasználó a lista átnézése
          UTÁN jár. Nincs `mt-*` — a térközt a `FormSection` `space-y-4`-e adja. */}
      {canManage && (
        <div className="flex">
          <Button type="button" variant="outline" onClick={() => setCreateOpen(true)}>
            <PlusIcon />
            {t('clients.addPaymentCard')}
          </Button>
        </div>
      )}

      <PaymentCardFormSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        card={null}
        lockedClient={{ id: clientId, name: clientName }}
      />
      <PaymentCardFormSheet
        open={editRow !== null}
        onOpenChange={(o) => !o && setEditRow(null)}
        card={editRow}
      />
    </FormSection>
  );
}
